import React, { useCallback, useEffect, useMemo, useRef, useState, forwardRef } from 'react';

import { Box, Flex, Skeleton } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { HistogramChart } from '@mesh.ai/rabbit';
import { useQueryClient } from '@tanstack/react-query';
import { MetricCards } from 'Deer/metricCards/MetricCards';
import { setFiltersAction, setTeamAction } from 'actions/globalFilters';
import Carousel from 'components/Carousel';
import CreateGoalPanel from 'components/MeshList/goals/CreateGoalPanel';
import ObjectiveSidePanel from 'components/ObjectiveSidePanel';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import TeamModel from 'models/team';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { openCreateOKRPanel } from 'reducers/createObjectivePanelSlice';
import { useSelectedView } from 'reducers/viewsSlice';
import { getDefaultLocalStorageStatsFilters, useTeamPageState } from 'screens/Team';
import buildTeamPageFilters from 'screens/Team/TeamPageFilters/buildTeamPageFilters';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { okrsPageSelector } from 'selectors/okrsPage';
import { themeColors } from 'themes/clients/baseTheme';
import { checkGraphAccessEnabled } from 'utils/helpers/featureFlags';

import { CollapseAllList, ExpandAllList, ExpandAllListStroke, InfoWorkZero } from 'assets/assetsV1/iconComponents';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE, OKR_TYPES, TASK_TYPES_2 } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { getPersistenceKey, isMobileView } from 'constants/helper';
import { sendCreatedObjectiveEvent, sendCreatedKREvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { AgGridSearch } from 'uiToolkitV1/AgGridWrapperV2';
import { AgGridEllipsisMenu } from 'uiToolkitV1/AgGridWrapperV2/components/EllipsisMenu/AgGridEllipsisMenu';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { GROUP_BY_INDIVIDUAL_KEY_OBJ } from '.';
import { AgGridAddNewGoalObjectiveSplitButton } from '../AgGridAddNewGoalObjectiveSplitButton';
import ChartContainer from '../AgGridTreeView/components/ChartContainer/ChartContainer';
import { goalsCascadedViewReactQueryKey } from '../AgGridTreeView/hooks/dataHooks';
import GroupByObjectiveInsightsTable from './RabbitGroupByObjectiveInsightsTable';

const StyledCarousel = styled(Carousel)`
  padding: 8px 0px;
  margin-bottom: 16px;
`;

const SPLIT_BY = {
  uuid: null,
  keyName: 'none',
  displayName: 'None',
  enabled: true,
};

const PerformanceChart = forwardRef(({ chartData, pluralKeyResultsName, keyResultsName }, ref) => (
  <HistogramChart
    tooltipTitle={`${keyResultsName} Progress Distribution`}
    xAxisLabel="Progress Percentage"
    yAxisLabel={`Count of ${pluralKeyResultsName}`}
    data={chartData}
    integerBins
    integerYAxis
    ref={ref}
  />
));

const OKRSidePanels = ({
  isOpenCreateGoalPanel,
  setIsOpenCreateGoalPanel,
  isOpenCreateObjectivePanel,
  setIsOpenCreateObjectivePanel,
  selectedTimelines,
  afterGoalCreation,
}) => {
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  const [isGoalPanelOfCreatedObjective, setIsGoalPanelOfCreatedObjective] = useState(false);
  // timebound details of newly created objective
  const [timeboundDetailsOfCreatedObjective, setTimeboundDetailsOfCreatedObjective] = useState(null);

  return (
    <>
      <CreateGoalPanel
        open={isOpenCreateGoalPanel}
        onCancel={() => {
          setIsOpenCreateGoalPanel(false);
        }}
        employee={loggedInEmployee}
        // defaultTask={defaultCreateTask}
        subObjective={isGoalPanelOfCreatedObjective ? { ...timeboundDetailsOfCreatedObjective } : null}
        source="teampage"
        eventSource="Team Page"
        selectedTimeBoundPeriod={selectedTimelines?.[0] ?? null}
        afterGoalCreation={afterGoalCreation}
      />

      <ObjectiveSidePanel
        open={isOpenCreateObjectivePanel}
        onCancel={() => {
          setTimeboundDetailsOfCreatedObjective(null);
          setIsGoalPanelOfCreatedObjective(false);
          setIsOpenCreateObjectivePanel(false);
        }}
        selectedTimeBoundPeriod={selectedTimelines?.[0] ?? null}
        isAlignmentAtParentLevel
        setIsOpenCreateGoalPanel={(value) => {
          setIsOpenCreateGoalPanel(value);
          setIsGoalPanelOfCreatedObjective(value);
        }}
        setTimeboundDetailsOfCreatedObjective={setTimeboundDetailsOfCreatedObjective}
        source="Team Page"
      />
    </>
  );
};

const ClickThroughIndividualAgGridTable = ({ isHierarchyView = false, employeeId }) => {
  const location = useLocation();
  const history = useHistory();
  const gridRef = useRef();
  const performanceChartRef = useRef();
  const page = useViewPageName();
  const sideNavSelectedCohort = useSelectedView();
  const { selectedTimelines } = useSelector(okrsPageSelector);

  const [isOpenCreateGoalPanel, setIsOpenCreateGoalPanel] = useState(false);
  const [isOpenCreateObjectivePanel, setIsOpenCreateObjectivePanel] = useState(false);
  const [isExpandAllActive, setIsExpandAllActive] = useState(true);
  const [chartData, setChartData] = useState({});
  const [cardKPIData, setCardKPIData] = useState(null);

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const { id: paramsTeamId, name: teamName } = sideNavSelectedCohort ?? {};
  const dispatch = useDispatch();

  const { team, setTeam, activeFiltersDispatch, newFiltersState } = useTeamPageState({
    sideNavSelectedCohort,
  });

  const queryClient = useQueryClient();

  const attributeFilter = useMemo(
    () => ({
      filterConditions: [
        {
          type: 'NAME',
          operatorType: 'IN',
          data: [employeeId],
        },
        {
          type: 'TYPE',
          operatorType: 'IN',
          data: ['GOAL'],
        },
      ],
    }),
    [employeeId]
  );

  const refetchOkrRelatedData = () => {
    queryClient.invalidateQueries(
      goalsCascadedViewReactQueryKey({
        cohort: sideNavSelectedCohort,
        groupByKeys: GROUP_BY_INDIVIDUAL_KEY_OBJ,
        splitByKeys: SPLIT_BY,
        orderBy: null,
        timeBounds: [selectedTimelines?.[0]],
        attributeFilter,
        isHierarchyView,
        /* Setting profile page here because we are showing same data as profile page */
        pageType: 'PROFILE_PAGE',
      })
    );

    queryClient.invalidateQueries(REACT_QUERY_KEYS.GoalsInsightsGroupByGoalsData);
    queryClient.invalidateQueries(REACT_QUERY_KEYS.GoalsInsightsGroupByGoalsData);
  };

  const refetchCascadedViewData = () => {
    queryClient.invalidateQueries(
      goalsCascadedViewReactQueryKey({
        cohort: sideNavSelectedCohort,
        groupByKeys: GROUP_BY_INDIVIDUAL_KEY_OBJ,
        splitByKeys: SPLIT_BY,
        orderBy: null,
        timeBounds: [selectedTimelines?.[0]],
        attributeFilter,
        isHierarchyView,
        /* Setting profile page here because we are showing same data as profile page */
        pageType: 'PROFILE_PAGE',
      })
    );
  };

  const handleKeyResultValuesUpdated = useCallback(() => {
    const newRowData = [];
    gridRef?.current?.api?.forEachNodeAfterFilterAndSort((row) => {
      newRowData.push(row?.data);
    });
    const totalKeyResults = newRowData.filter((row) => row.type === OKR_TYPES.KEY_RESULT);
    const keyResultsWithNoCheckIns = totalKeyResults.filter((row) => !row.lastCheckIn);
    const keyResultsAtRisk = totalKeyResults.filter((row) => row.progressLevel === 'AT_RISK');
    const sumOfAllProgresses = totalKeyResults.reduce((prev, cur) => (cur?.avgProgressPercentage || 0) + prev, 0);
    const avgProgressPercentage = sumOfAllProgresses / (totalKeyResults.length || 1);

    setCardKPIData([
      {
        title: `Total no. of ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)}`,
        value: totalKeyResults.length,
      },
      {
        title: `No. of ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)} with no check-ins`,
        value: keyResultsWithNoCheckIns.length,
      },
      {
        title: `No. of ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)} at-risk`,
        value: keyResultsAtRisk.length,
      },
      {
        title: `Average progress percentage`,
        value: `${avgProgressPercentage?.toFixed(2) || 0}%`,
      },
    ]);
  }, [keyResultsName]);

  useEffect(() => {
    const teamId = paramsTeamId;
    if (!teamId) return;

    dispatch(setTeamAction(teamId)).then(() => {
      setTeam(new TeamModel({ id: teamId, name: teamName }));
    });
  }, [dispatch, setTeam, paramsTeamId, teamName]);

  useEffect(() => {
    const newActiveFilters = buildTeamPageFilters(newFiltersState, true);

    const localStorageStatsFilters = getDefaultLocalStorageStatsFilters(TASK_TYPES_2.goal) || [];
    activeFiltersDispatch(setFiltersAction([...newActiveFilters, ...localStorageStatsFilters]));
  }, [JSON.stringify(newFiltersState)]);

  /* Grid State persistence Key */
  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.GOAL;
    const viewId = paramsTeamId;
    const tabId = 'Insights';
    const uniqueId = 'GroupByIndividualClickThroughGrid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, [paramsTeamId]);

  const ellipsisMenuOptions = useMemo(
    () => [
      {
        label: `Expand/ collapse all ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)}`,
        type: 'subMenu',
        onClick: () => {},
        subOptions: [
          {
            label: `Expand all ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)}`,
            onClick: () => gridRef.current.api.expandAll(),
            Icon: ExpandAllList,
          },
          {
            label: `Collapse all ${modifyString(keyResultsName, StringModifier.PLURAL_SENTENCE_CASE)}`,
            onClick: () => gridRef.current.api.collapseAll(),
            Icon: CollapseAllList,
          },
        ],
        Icon: ExpandAllListStroke,
      },
    ],
    [keyResultsName]
  );

  if (!sideNavSelectedCohort || !team || !team?.id) {
    return <Skeleton height="30vh" />;
  }

  return (
    <>
      <Box width="100%">
        <Box borderRadius="8px" p="0px" backgroundColor={themeColors.neutral.EBEFF3}>
          {!isMobileView() && checkGraphAccessEnabled() && (
            <ChartContainer
              chartData={chartData}
              chartRef={performanceChartRef}
              chartTitle={`${modifyString(keyResultsName, StringModifier.TITLE_CASE)} Progress Distribution`}
            >
              <PerformanceChart
                chartData={
                  chartData?.rowData
                    ?.filter((data) => data.type === OKR_TYPES.KEY_RESULT)
                    ?.map((data) => data?.avgProgressPercentage || 0) || []
                }
                keyResultsName={modifyString(keyResultsName, StringModifier.TITLE_CASE)}
                pluralKeyResultsName={modifyString(keyResultsName, StringModifier.PLURAL_TITLE_CASE)}
              />
            </ChartContainer>
          )}
          {!isMobileView() && checkGraphAccessEnabled() && cardKPIData && (
            <>
              {chartData?.isLoading && (
                <Box display="flex">
                  <Skeleton
                    height="100px"
                    width="100%"
                    borderRadius="8px"
                    startColor="surface.F5F5F5"
                    endColor="surface.g3"
                  />
                </Box>
              )}
              {!chartData?.isLoading && (
                <MetricCards height="100%" columns="repeat(4, 1fr)" cardTitle="Metrics" cards={cardKPIData} />
              )}
              {/* <StyledCarousel slidesToShow={4} slidesToScroll={1} slideMargin="10px" stretchChildren>
               {cardKPIData.map((data, index) =>
                (
                   <CardKPI count={data.value} title={data.title} unstyledDimension />
                 )
               )}
             </StyledCarousel> */}
            </>
          )}
          <Box backgroundColor="white" p="24px" width="100%" borderRadius="8px">
            <Flex ml="auto" alignItems="center" mb="22px">
              <AgGridSearch placeholder="Search" gridRef={gridRef} key={sideNavSelectedCohort?.id} />
              <Flex w="100%" alignItems="center" justifyContent="flex-end" mr="16px">
                <AgGridAddNewGoalObjectiveSplitButton
                  onAddKRClick={() => {
                    dispatch(
                      openCreateOKRPanel({
                        selectedObjective: null,
                        selectedTimeBoundPeriod: selectedTimelines?.[0],
                        okrPanelType: OKR_TYPES.KEY_RESULT,
                        onKeyResultCreation(okrDetails) {
                          sendCreatedKREvent({
                            eventProperties: {
                              source: 'Add New Button',
                              page,
                              goalId: okrDetails?.uuid,
                            },
                          });
                          refetchOkrRelatedData();
                        },
                        onObjectiveCreation(okrDetails) {
                          sendCreatedObjectiveEvent({
                            eventProperties: {
                              source: 'Add New Button',
                              page,
                              objectiveId: okrDetails?.uuid,
                              description: okrDetails?.description,
                              level: okrDetails?.objectiveLevel,
                              type: okrDetails?.type,
                              timePeriod: okrDetails?.timeBoundDetails?.timelineTag,
                            },
                          });
                          refetchOkrRelatedData();
                        },
                        history,
                        location,
                      })
                    );
                  }}
                  onAddObjectiveClick={() => {
                    dispatch(
                      openCreateOKRPanel({
                        selectedObjective: null,
                        selectedTimeBoundPeriod: selectedTimelines?.[0],
                        okrPanelType: OKR_TYPES.OBJECTIVE,
                        onKeyResultCreation(okrDetails) {
                          sendCreatedKREvent({
                            eventProperties: {
                              source: 'Add New Button',
                              page,
                              goalId: okrDetails?.uuid,
                            },
                          });
                          refetchOkrRelatedData();
                        },
                        onObjectiveCreation(okrDetails) {
                          sendCreatedObjectiveEvent({
                            eventProperties: {
                              source: 'Add New Button',
                              page,
                              objectiveId: okrDetails?.uuid,
                              description: okrDetails?.description,
                              level: okrDetails?.objectiveLevel,
                              type: okrDetails?.type,
                              timePeriod: okrDetails?.timeBoundDetails?.timelineTag,
                            },
                          });
                          refetchOkrRelatedData();
                        },
                        history,
                        location,
                      })
                    );
                  }}
                />
              </Flex>
              <Box>
                {!isMobileView() && <AgGridEllipsisMenu options={ellipsisMenuOptions} selectedRadioValue={null} />}
              </Box>
            </Flex>

            <GroupByObjectiveInsightsTable
              gridStatePersistenceKey={gridStatePersistenceKey}
              attributeFilter={attributeFilter}
              selectedTimelines={selectedTimelines}
              gridRef={gridRef}
              groupBy={GROUP_BY_INDIVIDUAL_KEY_OBJ}
              pageType="PROFILE_PAGE"
              setChartData={setChartData}
              onModalUpdated={handleKeyResultValuesUpdated}
            />
          </Box>
          <Flex
            alignItems="center"
            mt="16px"
            borderRadius="8px"
            padding="8px"
            backgroundColor="#F5F7F9"
            boxShadow="0px 2px 8px rgba(0, 0, 0, 0.08)"
          >
            <InfoWorkZero boxSize="24px" />
            <TextBodyMedium>
              You will see metrics only for the {modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)} you’ve
              access to, hence for same individual or {modifyString(objectivesName, StringModifier.SENTENCE_CASE)},
              admin could see different metrics due to difference in access level
            </TextBodyMedium>
          </Flex>
        </Box>
      </Box>
      <OKRSidePanels
        isOpenCreateGoalPanel={isOpenCreateGoalPanel}
        setIsOpenCreateGoalPanel={setIsOpenCreateGoalPanel}
        isOpenCreateObjectivePanel={isOpenCreateObjectivePanel}
        setIsOpenCreateObjectivePanel={setIsOpenCreateObjectivePanel}
        selectedTimelines={selectedTimelines}
        afterGoalCreation={refetchOkrRelatedData}
      />
    </>
  );
};

export { ClickThroughIndividualAgGridTable };
